'use client'

import { useEffect, useRef, useState } from 'react'
import { signIn } from 'next-auth/react'
import { X, Check, Loader2 } from 'lucide-react'

/**
 * Connexion Google ASSISTÉE PAR TÉLÉPHONE, côté appli (TV/mobile WebView).
 * Ouvert par l'événement 'blc-tv-login-open'. Affiche un QR → /login-phone?c=CODE,
 * POLLE le relais puis authentifie l'appli via le provider `phonelink` (code +
 * token SECRET connu seulement ici). Overlay `blc-tv-overlay` → navigable D-pad.
 */
function rnd(n: number) {
  const A = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let s = ''
  for (let i = 0; i < n; i++) s += A[Math.floor(Math.random() * A.length)]
  return s
}

export default function TvLogin() {
  const [open, setOpen] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [code, setCode] = useState('')
  const [status, setStatus] = useState<'waiting' | 'connecting' | 'done'>('waiting')
  const tokenRef = useRef('')

  useEffect(() => {
    const onOpen = () => {
      const c = rnd(5), t = rnd(24)
      tokenRef.current = t
      setCode(c); setQr(null); setStatus('waiting'); setOpen(true)
      fetch('/api/loginlink', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action: 'create', code: c, token: t }) })
        .then(r => r.json()).then(j => { if (j?.qr) setQr(j.qr) }).catch(() => {})
    }
    window.addEventListener('blc-tv-login-open', onOpen)
    return () => window.removeEventListener('blc-tv-login-open', onOpen)
  }, [])

  // Poll : dès que le téléphone a réclamé le code → on connecte l'appli.
  useEffect(() => {
    if (!open || status !== 'waiting' || !code) return
    let dead = false
    const iv = setInterval(async () => {
      try {
        const r = await fetch(`/api/loginlink?code=${code}&token=${tokenRef.current}`, { cache: 'no-store' })
        const j = await r.json()
        if (dead || !j?.ready) return
        clearInterval(iv)
        setStatus('connecting')
        const res = await signIn('phonelink', { code, token: tokenRef.current, redirect: false })
        if (res?.ok) { setStatus('done'); setTimeout(() => { try { location.reload() } catch {} }, 900) }
        else setStatus('waiting')
      } catch {}
    }, 1500)
    return () => { dead = true; clearInterval(iv) }
  }, [open, status, code])

  // RETOUR de la télécommande → fermeture instantanée (les menus l'ignorent
  // tant qu'un [data-tvmodal] est affiché).
  useEffect(() => {
    if (!open) return
    const onBack = (e: Event) => { if ((e as any).cancelable) e.preventDefault(); setOpen(false) }
    window.addEventListener('blc-tv-back', onBack)
    return () => window.removeEventListener('blc-tv-back', onBack)
  }, [open])

  if (!open) return null

  return (
    <div data-tvmodal="1" className="blc-tv-overlay fixed inset-0 z-[2147483600] flex items-center justify-center p-4 sm:p-8 overflow-y-auto" style={{ background: 'rgb(var(--sbg)/0.92)' }}>
      <div className="tvp-dialog relative w-full max-w-xl rounded-2xl bg-spanel border border-white/[0.07] p-6 sm:p-8 text-center shadow-[0_30px_100px_-20px_rgb(var(--a1)/.5)] overflow-hidden">
        <div aria-hidden className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[420px] h-[260px] rounded-full bg-a1/25 blur-[80px]" />
        <div className="relative">
          <div className="text-[11px] font-semibold tracking-[0.28em] text-white/55 mb-2">CONNEXION GOOGLE</div>
          <div className="text-2xl sm:text-[28px] font-extrabold tracking-tight mb-6">Scanne avec ton téléphone</div>
          {status === 'done' ? (
            <div className="py-8">
              <span className="w-16 h-16 mx-auto mb-3 rounded-full grid place-items-center bg-emerald-500/15 text-emerald-400"><Check size={36} /></span>
              <div className="text-xl font-bold">Connecté !</div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              {qr
                ? <img src={qr} alt="QR connexion" className="w-52 h-52 sm:w-56 sm:h-56 rounded-2xl bg-white p-2 ring-4 ring-a2/40 shadow-[0_0_40px_rgb(var(--a2)/.45)]" />
                : <div className="w-52 h-52 sm:w-56 sm:h-56 rounded-2xl bg-white/[0.06] border border-white/[0.09] flex items-center justify-center text-white/40"><Loader2 size={28} className="animate-spin text-a4" /></div>}
              <div className="text-left max-w-xs">
                <ol className="space-y-3">
                  {[
                    <>Scanne ce QR avec ton téléphone.</>,
                    <>Connecte-toi avec <b>Google</b> (ça marche dans ton navigateur).</>,
                    <>La TV se connecte toute seule au même compte.</>,
                  ].map((t, i) => (
                    <li key={i} className="flex gap-3 text-white/75 text-[15px]">
                      <span className="w-7 h-7 shrink-0 rounded-lg bg-a2/20 text-a4 grid place-items-center text-sm font-bold">{i + 1}</span>
                      <span className="pt-0.5">{t}</span>
                    </li>
                  ))}
                </ol>
                {status === 'connecting' && <div className="mt-4 text-a4 inline-flex items-center gap-2 text-sm"><Loader2 size={15} className="animate-spin" /> Connexion de l’appli…</div>}
                <div className="mt-4 flex items-center justify-between gap-3 rounded-xl bg-white/[0.05] border border-white/[0.08] px-4 py-2.5">
                  <span className="text-[12.5px] text-white/50">Code</span>
                  <span className="font-mono text-white tracking-[0.3em]">{code}</span>
                </div>
              </div>
            </div>
          )}
          <button onClick={() => setOpen(false)} className="mt-7 rounded-full px-8 py-3 text-base font-semibold text-white/90 bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] focus:bg-white/[0.12] inline-flex items-center gap-2 transition"><X size={18} /> Fermer</button>
        </div>
      </div>
    </div>
  )
}
